import { httpClient } from "../../../shared/api/httpClient";
import type { PaginatedResponse } from "../../../shared/api/pagination";
import type {
  GroupOrderResponse,
  GroupSaleAssignmentResponse,
  GroupSaleOptionResponse,
} from "../../../entities/group-order/model/types";
import type { Group, SellerGroup } from "../model/types";
import { toGroup } from "../model/types";

export interface FetchGroupsParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface FetchGroupsResponse {
  data: Group[];
  total: number;
}

const toSeconds = (value: number, unit: "seconds" | "minutes" | "hours") => {
  if (unit === "hours") return value * 3600;
  if (unit === "minutes") return value * 60;
  return value;
};

export const groupOrdersApi = {
  async fetchGroups(
    params: FetchGroupsParams = {},
  ): Promise<FetchGroupsResponse> {
    const response = await httpClient.get<
      PaginatedResponse<GroupOrderResponse>
    >("/group-orders", {
      params: {
        page: params.page ?? 1,
        page_size: params.pageSize ?? 10,
        search: params.search?.trim() || undefined,
      },
    });
    return {
      total: response.data.pagination.total,
      data: response.data.items.map(toGroup),
    };
  },

  async updateResponseTime(
    groupId: string,
    payload: { value: number; unit: "seconds" | "minutes" | "hours" },
  ): Promise<void> {
    await httpClient.patch(
      `/group-orders/${encodeURIComponent(groupId)}/waiting-time`,
      {
        waiting_time: toSeconds(payload.value, payload.unit),
      },
    );
  },

  async fetchAvailableSellerGroups(groupId: string): Promise<SellerGroup[]> {
    const response = await httpClient.get<GroupSaleOptionResponse[]>(
      "/group-orders/group-sales/available",
      { params: { group_id: groupId } },
    );
    return response.data.map((item) => ({
      id: item.group_sale_id,
      name: item.group_sale_name,
    }));
  },

  async updateGroupSeller(
    groupId: string,
    sellerGroupId: string | null,
  ): Promise<void> {
    await httpClient.patch(
      `/group-orders/${encodeURIComponent(groupId)}/group-sale`,
      {
        group_sale_id: sellerGroupId,
      },
    );
  },

  async fetchSellerGroup(groupId: string): Promise<SellerGroup | null> {
    const response = await httpClient.get<GroupSaleAssignmentResponse>(
      `/group-orders/${encodeURIComponent(groupId)}/group-sale`,
    );
    if (response.data.group_sale_id == null) return null;
    return {
      id: response.data.group_sale_id,
      name: response.data.group_sale_name || "",
    };
  },
};
